import React, { useEffect, useState } from 'react';

import { Image } from 'antd';
import classnames from 'classnames';
import { storage } from 'firebase/firebase';
import { getDownloadURL, ref } from 'firebase/storage';
import PropTypes from 'prop-types';

import { COMPONENT_TYPES } from '../../../../../eventTypes/constants';
import { getAttachmentViewState } from '../../utils/getAttachmentViewState';
import AttachmentLoadingState from '../AttachmentLoadingState';
import AttachmentStatusMessage from '../AttachmentStatusMessage';
import styles from './index.module.less';

const Photos = ({ component, labelClassname, showTitle }) => {
  const getImage = async imageUrl => getDownloadURL(ref(storage, imageUrl));

  const sourceCount = component.photoValue?.length ?? 0;
  const [isLoading, setLoading] = useState(sourceCount > 0);
  const [urls, setUrls] = useState([]);
  const [error, setError] = useState(false);

  const loadImages = async () => {
    const promises = [];
    let loadedUrls = [];
    component.photoValue?.forEach(img => {
      promises.push(getImage(img.url));
    });

    try {
      loadedUrls = await Promise.all(promises);
    } catch (e) {
      setError(true);
      Error(e);
    }

    setUrls(loadedUrls);
    setLoading(false);
  };

  const viewState = getAttachmentViewState({ error, isLoading, sourceCount });

  useEffect(() => {
    if (sourceCount === 0) {
      setLoading(false);
      return;
    }
    loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classnames(showTitle && 'gx-mt-3')}>
      {showTitle && (
        <h4 className={classnames(styles.title, labelClassname)}>{component.label.name}</h4>
      )}
      <AttachmentStatusMessage state={viewState} />
      {!error && isLoading && sourceCount > 0 && (
        <AttachmentLoadingState count={sourceCount} type="photo" />
      )}
      {!error && urls.length > 0 && (
        <Image.PreviewGroup>
          <div className={styles.grid} data-testid="photos-grid">
            {urls.map(fileName => (
              <div key={fileName} className={styles.thumbItem}>
                <Image
                  className={styles.thumb}
                  wrapperClassName={styles.thumbWrapper}
                  src={fileName}
                  width="100%"
                  height="100%"
                  preview={{
                    getContainer: () => document.body,
                  }}
                />
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      )}
    </div>
  );
};

Photos.propTypes = {
  component: PropTypes.shape({
    componentProps: PropTypes.shape({
      autocomplete: PropTypes.bool,
      checked: PropTypes.bool,
      format: PropTypes.string,
      isDecimal: PropTypes.bool,
      max: PropTypes.number,
      maxCount: PropTypes.number,
      maxLength: PropTypes.number,
      maxSize: PropTypes.number,
      min: PropTypes.number,
      minLength: PropTypes.number,
      pattern: PropTypes.string,
      placeholder: PropTypes.string,
    }).isRequired,
    commonProps: PropTypes.shape({ required: PropTypes.bool.isRequired }).isRequired,
    label: PropTypes.shape({
      description: PropTypes.string,
      name: PropTypes.string.isRequired,
    }),
    type: COMPONENT_TYPES.PHOTO,
    photoValue: PropTypes.arrayOf(
      PropTypes.shape({
        size: PropTypes.number.isRequired,
        tags: PropTypes.arrayOf(PropTypes.string).isRequired,
        url: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,

  labelClassname: PropTypes.string,
  /** Hide the built-in title when the caller renders its own label. */
  showTitle: PropTypes.bool,
};

Photos.defaultProps = {
  labelClassname: '',
  showTitle: true,
};

export default React.memo(Photos);
